<template>
  <v-card id="payment-reconciliation">
    <v-card-title class="text-h6">
      Сверка оплат с Delivery
    </v-card-title>
    <v-card-subtitle class="pb-0">
      Каждый заказ, оплаченный онлайн, — и что с ним стало в Delivery: выполнен, отменён,
      заведён заново, не взят в работу.
    </v-card-subtitle>

    <v-card-text class="pt-4 pb-0">
      <v-row dense>
        <v-col
          cols="12"
          md="3"
        >
          <v-text-field
            v-model="filters.from"
            type="date"
            label="Период с"
            dense
            outlined
            hide-details
            :error="!filters.from"
          ></v-text-field>
        </v-col>

        <v-col
          cols="12"
          md="3"
        >
          <v-text-field
            v-model="filters.to"
            type="date"
            label="Период по"
            dense
            outlined
            hide-details
            :error="!filters.to"
          ></v-text-field>
        </v-col>

        <v-col
          cols="12"
          md="3"
        >
          <v-select
            v-model="filters.restaurantId"
            :items="restaurantItems"
            :disabled="!loaded"
            :hint="loaded ? '' : 'Появятся после первой загрузки'"
            dense
            outlined
            clearable
            persistent-hint
            label="Филиал"
          ></v-select>
        </v-col>
      </v-row>
    </v-card-text>

    <v-card-text>
      <div class="d-flex align-center flex-wrap">
        <v-btn
          color="primary"
          :loading="loading"
          :disabled="!periodChosen"
          @click="refresh"
        >
          <v-icon
            size="18"
            class="me-2"
          >
            {{ icons.mdiRefresh }}
          </v-icon>
          <span>Обновить</span>
        </v-btn>

        <v-btn
          outlined
          class="ms-3"
          :loading="downloading"
          :disabled="!loaded || !periodChosen"
          @click="download"
        >
          <v-icon
            size="18"
            class="me-2"
          >
            {{ icons.mdiExportVariant }}
          </v-icon>
          <span>Выгрузить в Excel</span>
        </v-btn>

        <v-spacer></v-spacer>

        <span
          v-if="loaded"
          class="text--secondary"
        >
          {{ checkedText }}
        </span>
      </div>

      <v-alert
        v-if="error"
        type="error"
        dense
        text
        class="mt-4 mb-0"
      >
        {{ error }}
      </v-alert>
    </v-card-text>

    <v-card-text
      v-if="loaded"
      class="pt-0"
    >
      <div class="d-flex flex-wrap mb-2">
        <div class="reconciliation-total me-6 mb-2">
          <div class="text--secondary text-caption">
            Оплачено
          </div>
          <div class="text-h6">
            {{ totals.paidCount }} · {{ formatMoney(totals.paidAmount) }}
          </div>
        </div>
        <div class="reconciliation-total me-6 mb-2">
          <div class="text--secondary text-caption">
            К разбору
          </div>
          <div class="text-h6 error--text">
            {{ totals.problemCount }} · {{ formatMoney(totals.problemAmount) }}
          </div>
        </div>
        <div class="reconciliation-total mb-2">
          <div class="text--secondary text-caption">
            Разбор
          </div>
          <div class="text-h6">
            новых {{ caseTotals.newCount }} · в работе {{ caseTotals.inProgressCount }} ·
            решено {{ caseTotals.resolvedCount }}
          </div>
        </div>
      </div>

      <v-chip
        v-for="item in categories"
        :key="item.category"
        :color="colorOf(item.category)"
        :outlined="!selected.includes(item.category)"
        small
        class="me-2 mb-2"
        @click="toggle(item.category)"
      >
        {{ item.title }}: {{ item.count }} · {{ formatMoney(item.amount) }}
      </v-chip>
    </v-card-text>

    <v-tabs v-model="tab">
      <v-tab>Оплаты</v-tab>
      <v-tab>По филиалам</v-tab>
      <v-tab>Кто отменял</v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab">
      <v-tab-item>
        <v-card-text>
          <div class="d-flex align-center flex-wrap mb-4">
            <v-text-field
              v-model="search"
              dense
              outlined
              hide-details
              clearable
              class="reconciliation-search me-4"
              label="Поиск: номер, филиал, оператор, операция банка"
              :prepend-inner-icon="icons.mdiMagnify"
            ></v-text-field>
            <v-select
              v-model="caseStatusFilter"
              :items="caseStatusItems"
              :disabled="!loaded || loading"
              dense
              outlined
              hide-details
              class="reconciliation-case-filter me-4"
              label="Разбор"
            ></v-select>
            <v-switch
              v-model="onlyProblems"
              dense
              hide-details
              class="mt-0"
              label="Только к разбору"
            ></v-switch>
          </div>

          <v-data-table
            :headers="rowHeaders"
            :items="visibleRows"
            :loading="loading"
            :items-per-page="25"
            :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
            item-key="orderId"
            class="text-no-wrap"
          >
            <template #[`item.orderId`]="{item}">
              <router-link
                v-if="canOpenOrders"
                :to="{ name: 'order-form-tab', params: { id: item.orderId } }"
              >
                № {{ item.orderId }}
              </router-link>
              <span v-else>№ {{ item.orderId }}</span>
              <span
                v-if="item.deliveryNumber"
                class="text--secondary"
              > · Delivery {{ item.deliveryNumber }}</span>
            </template>

            <template #[`item.createdAt`]="{item}">
              {{ formatDate(item.createdAt) }}
            </template>

            <template #[`item.paidAt`]="{item}">
              {{ formatDate(item.paidAt) }}
            </template>

            <template #[`item.restaurant`]="{item}">
              {{ item.restaurant }}
              <template v-if="item.isReplacedElsewhere">
                →
                <v-chip
                  x-small
                  label
                  color="warning"
                  text-color="white"
                  class="font-weight-bold"
                >
                  {{ item.replacementRestaurant || 'другой филиал' }}
                </v-chip>
              </template>
            </template>

            <template #[`item.paidAmount`]="{item}">
              {{ formatMoney(item.paidAmount) }}
            </template>

            <template #[`item.categoryTitle`]="{item}">
              <v-chip
                small
                :color="colorOf(item.category)"
                :text-color="item.isProblem ? 'white' : undefined"
              >
                {{ item.categoryTitle }}
              </v-chip>
            </template>

            <template #[`item.caseStatus`]="{item}">
              <div
                v-if="item.isProblem"
                class="d-flex align-center"
              >
                <v-chip
                  small
                  label
                  :color="caseColor(item.caseStatus)"
                  :text-color="item.caseStatus === 'New' ? undefined : 'white'"
                  class="me-1"
                  @click="openCase(item)"
                >
                  {{ item.caseStatusName }}
                </v-chip>
                <v-tooltip
                  bottom
                  max-width="360"
                  :disabled="!item.lastComment"
                >
                  <template #activator="{ on, attrs }">
                    <v-btn
                      text
                      small
                      class="px-1"
                      :class="{ 'text--disabled': !item.commentsCount }"
                      :aria-label="`Разбор заказа ${item.orderId}: комментариев ${item.commentsCount}`"
                      v-bind="attrs"
                      v-on="on"
                      @click="openCase(item)"
                    >
                      <v-icon size="18">
                        {{ item.commentsCount ? icons.mdiCommentTextOutline : icons.mdiCommentOutline }}
                      </v-icon>
                      <span
                        v-if="item.commentsCount"
                        class="ms-1"
                      >{{ item.commentsCount }}</span>
                    </v-btn>
                  </template>
                  <div v-if="item.lastComment">
                    <div>{{ firstLine(item.lastComment.text) }}</div>
                    <div class="text-caption">
                      {{ item.lastComment.author }} · {{ formatDate(item.lastComment.createdAt) }}
                    </div>
                  </div>
                </v-tooltip>
              </div>
            </template>

            <template #[`item.action`]="{item}">
              <div class="reconciliation-action">
                {{ item.action }}
              </div>
            </template>

            <template #[`item.replacement`]="{item}">
              <span v-if="item.replacementNumber">
                № {{ item.replacementNumber }} ·
                <span :class="{ 'warning--text font-weight-bold': item.isReplacedElsewhere }">
                  {{ item.replacementRestaurant || '—' }}</span> ·
                {{ item.replacementSource || '—' }} ·
                {{ item.replacementStatus || '—' }} · {{ item.replacementPayment || '—' }}
                <template v-if="item.replacementAmount !== null"> · {{ formatMoney(item.replacementAmount) }}</template>
              </span>
            </template>

            <template #[`body.append`]>
              <tr class="font-weight-bold">
                <td colspan="4">
                  ИТОГО по отбору: {{ visibleRows.length }}
                </td>
                <td class="text-right">
                  {{ formatMoney(visibleAmount) }}
                </td>
                <td :colspan="rowHeaders.length - 5"></td>
              </tr>
            </template>
          </v-data-table>
        </v-card-text>
      </v-tab-item>

      <v-tab-item>
        <v-card-text>
          <v-data-table
            :headers="branchHeaders"
            :items="branches"
            :loading="loading"
            :items-per-page="-1"
            hide-default-footer
            class="text-no-wrap"
          >
            <template #[`item.paidAmount`]="{item}">
              {{ formatMoney(item.paidAmount) }}
            </template>
            <template #[`item.problemAmount`]="{item}">
              {{ formatMoney(item.problemAmount) }}
            </template>
            <template #[`item.cancelledAmount`]="{item}">
              {{ formatMoney(item.cancelledAmount) }}
            </template>
          </v-data-table>
        </v-card-text>
      </v-tab-item>

      <v-tab-item>
        <v-card-text>
          <v-data-table
            :headers="operatorHeaders"
            :items="operators"
            :loading="loading"
            :items-per-page="-1"
            hide-default-footer
            class="text-no-wrap"
          >
            <template #[`item.cancelledAmount`]="{item}">
              {{ formatMoney(item.cancelledAmount) }}
            </template>
          </v-data-table>

          <p class="text--secondary mb-0 mt-2">
            Оператор — тот, кто последним работал с заказом в Delivery: у отменённого это почти
            всегда тот, кто отменил. «Не указан» — Delivery имени не назвал.
          </p>
        </v-card-text>
      </v-tab-item>
    </v-tabs-items>

    <payment-reconciliation-case
      :row="caseRow"
      :category-color="caseRow ? colorOf(caseRow.category) : undefined"
      :cancellation="closeCase"
    ></payment-reconciliation-case>

    <v-card-text class="text--secondary pt-0">
      «Отменён без замены» — деньги взяли, а заказа нет: гостю надо вернуть оплату.
      «Отменён, заведён заново» — у того же гостя в пределах трёх часов есть другой, не
      отменённый заказ: его завёл оператор или гость оформил снова, и деньги могли взять
      дважды. «Заведён в другом филиале» — то же, но новый заказ закрыл другой филиал, а деньги
      пришли на счёт нашего: кроме двойной оплаты, нужен расчёт между филиалами. Филиал, куда
      заказ ушёл на самом деле, подсвечен оранжевым рядом с нашим.
      «Не обработан» — оплачен, но больше часа не передан на кухню. «Оплата не
      отмечена» — заказ в работе, а Delivery не видит оплату, и курьер может взять деньги ещё
      раз. Статусы Delivery обновляются фоновой сверкой раз в полчаса; заказы старше месяца
      без сверки показаны как «Ещё не сверен».
      «Разбор» — что с проблемной строкой делают: новый, в работе или решён, и история
      комментариев. Нажмите на статус, чтобы открыть разбор.
    </v-card-text>
  </v-card>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import {
  mdiCommentOutline, mdiCommentTextOutline, mdiExportVariant, mdiMagnify, mdiRefresh,
} from '@mdi/js'
import { mapFormHandlers } from '@/helpers/forms'
import { hasPermission } from '@/helpers/index'
import { caseStatusColor, caseStatuses, firstLine } from '@/helpers/reconciliation'
import PaymentReconciliationCase from '@/views/report/payment-reconciliation-case.vue'

const CASE_ORDER = caseStatuses.map(status => status.value)

const ALL = 'All'

const LOSS = ['CancelledNotReplaced', 'NotInDelivery']

const ATTENTION = ['CancelledReplacedElsewhere', 'CancelledReplaced', 'NotProcessed', 'PaymentNotMarked', 'NotFound']

export default {
  name: 'PaymentReconciliation',
  components: { PaymentReconciliationCase },
  data: () => ({
    icons: {
      mdiCommentOutline, mdiCommentTextOutline, mdiExportVariant, mdiMagnify, mdiRefresh,
    },
    tab: 0,
    search: '',
    onlyProblems: false,

    selected: [],

    caseRow: null,
  }),
  computed: {
    ...mapFormHandlers('paymentReconciliation', [
      'filters.from',
      'filters.to',
      'filters.restaurantId',
      'filters.caseStatus',
    ]),
    ...mapState('paymentReconciliation', {
      rows: 'rows',
      categories: 'categories',
      branches: 'branches',
      operators: 'operators',
      restaurants: 'restaurants',
      totals: 'totals',
      caseTotals: 'caseTotals',
      checkedAt: 'checkedAt',
      loading: 'loading',
      downloading: 'downloading',
      loaded: 'loaded',
      error: 'error',
    }),
    periodChosen() {
      return Boolean(this.filters.from && this.filters.to)
    },
    checkedText() {
      return this.checkedAt
        ? `Статусы Delivery на ${this.formatDate(this.checkedAt)}`
        : 'Сверки с Delivery ещё не было'
    },
    canOpenOrders() {
      return hasPermission('Permissions.OrderRead')
    },

    caseStatusFilter: {
      get() {
        return this.filters.caseStatus || ALL
      },
      set(value) {
        this.filters.caseStatus = value === ALL ? null : value
        if (this.loaded && this.periodChosen) this.fetchReport()
      },
    },
    caseStatusItems() {
      return [
        { text: 'Все', value: ALL },
        ...caseStatuses.map(status => ({ text: status.plural, value: status.value })),
      ]
    },
    restaurantItems() {
      return this.restaurants.map(restaurant => ({ text: restaurant.name, value: restaurant.id }))
    },
    visibleRows() {
      const query = (this.search || '').trim().toLowerCase()

      return this.rows
        .filter(row => !this.onlyProblems || row.isProblem)
        .filter(row => !this.selected.length || this.selected.includes(row.category))
        .filter(row => !query || [
          row.orderId, row.deliveryNumber, row.restaurant, row.lastOperator, row.paymentIds,
          row.replacementNumber, row.replacementRestaurant, row.replacementSource,
        ].some(value => value !== null && value !== undefined && String(value).toLowerCase().includes(query)))
    },
    visibleAmount() {
      return this.visibleRows.reduce((total, row) => total + row.paidAmount, 0)
    },
    rowHeaders() {
      return [
        { text: 'Заказ · Delivery', value: 'orderId' },
        { text: 'Канал', value: 'channel' },
        { text: 'Дата заказа', value: 'createdAt' },
        { text: 'Оплачено', value: 'paidAt' },
        { text: 'Сумма оплаты', value: 'paidAmount', align: 'end' },
        { text: 'Филиал', value: 'restaurant' },
        { text: 'Статус в Delivery', value: 'deliveryStatus' },
        { text: 'Оплата в Delivery', value: 'deliveryPayment' },
        { text: 'Итог сверки', value: 'categoryTitle' },
        { text: 'Разбор', value: 'caseStatus', sort: this.compareCases },
        { text: 'Что сделать', value: 'action', sortable: false },
        { text: 'Кто последним работал', value: 'lastOperator' },
        { text: 'Замена', value: 'replacement', sortable: false },
        { text: 'Операция банка', value: 'paymentIds' },
        { text: 'Причина отмены', value: 'cancelReason' },
      ]
    },
    branchHeaders() {
      return [
        { text: 'Филиал', value: 'restaurant' },
        { text: 'Оплачено', value: 'paidCount', align: 'end' },
        { text: 'Сумма', value: 'paidAmount', align: 'end' },
        { text: 'К разбору', value: 'problemCount', align: 'end' },
        { text: 'Сумма к разбору', value: 'problemAmount', align: 'end' },
        { text: 'Отменено', value: 'cancelledCount', align: 'end' },
        { text: 'Сумма отменённых', value: 'cancelledAmount', align: 'end' },
      ]
    },
    operatorHeaders() {
      return [
        { text: 'Оператор', value: 'operator' },
        { text: 'Отменено оплаченных', value: 'cancelledCount', align: 'end' },
        { text: 'Сумма', value: 'cancelledAmount', align: 'end' },
        { text: 'Из них заведено заново', value: 'replacedCount', align: 'end' },
      ]
    },
  },
  created() {
    document.title = 'Сверка оплат с Delivery'
  },
  methods: {
    ...mapActions('paymentReconciliation', { fetchReport: 'fetch', downloadReport: 'download' }),
    async refresh() {
      this.selected = []
      await this.fetchReport()
    },
    async download() {
      await this.downloadReport()
    },
    openCase(row) {
      this.caseRow = row
    },
    closeCase() {
      this.caseRow = null
    },

    compareCases(a, b) {
      const rank = value => (CASE_ORDER.includes(value) ? CASE_ORDER.indexOf(value) : CASE_ORDER.length)

      return rank(a) - rank(b)
    },
    caseColor: caseStatusColor,
    firstLine,
    toggle(category) {
      this.selected = this.selected.includes(category)
        ? this.selected.filter(item => item !== category)
        : [...this.selected, category]
    },
    colorOf(category) {
      if (LOSS.includes(category)) return 'error'
      if (ATTENTION.includes(category)) return 'warning'
      if (category === 'Completed') return 'success'

      return 'grey'
    },
    formatDate(value) {
      if (!value) return ''

      return new Date(value).toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    formatMoney(value) {
      return new Intl.NumberFormat('ru-RU', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value || 0)
    },
  },
}
</script>

<style lang="scss" scoped>
#payment-reconciliation {
  .reconciliation-search {
    max-width: 26rem;
  }

  .reconciliation-action {
    white-space: normal;
    min-width: 16rem;
  }

  .reconciliation-case-filter {
    max-width: 12rem;
  }
}
</style>
